import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select'; // Import do MatSelectModule

import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { requiredTrim } from '../../../shared/validators/string-trim.validator';

import { ChaveAcessoService } from '../../../shared/services/apis/integracao/chave-acesso.service';
import { ChaveAcessoRequest } from '../../../shared/services/requests/integracao/chave-acesso.request';
import { GetChaveAcessoResponse } from '../../../shared/services/responses/integracao/get-chave-acesso.response';
import { ReturnStruct } from '../../../shared/services/structs/return.struct';
import { UsuarioService } from '../../../shared/services/apis/usuario/usuario.service';
import { ListaUsuarioResponse } from '../../../shared/services/responses/usuario/lista-usuario.response';
import { UsuarioListaStruct } from '../../../shared/services/structs/usuario/usuario-lista.struct';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';

// Supondo a existência de um serviço e modelo para Usuário
// import { UsuarioService } from '../../../shared/services/apis/usuario.service';
// import { Usuario } from '../../../shared/services/models/usuario.model';

@Component({
  selector: 'app-chave-acesso-cadastro',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    FooterActionComponent,
    HeaderComponent,
  ],
  templateUrl: './chave-acesso-cadastro.component.html',
  styleUrls: ['./chave-acesso-cadastro.component.css'],
})
export class ChaveAcessoCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Integracao;
  public funcionalidadeEnum = FuncionalidadeEnum.Integracao_ChaveAcesso;
  public model: FormGroup;
  public _isLoading = false;
  public idChaveAcesso: number | null = null;
  public listaUsuarios: UsuarioListaStruct[] = []; // Placeholder para a lista de usuários

  constructor(
    private formBuilder: FormBuilder,
    private chaveAcessoService: ChaveAcessoService,
    // private usuarioService: UsuarioService, // Injete o serviço de usuário
    private alertService: AlertService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private usuarioService: UsuarioService
  ) {
    this.model = this.formBuilder.group({
      chaveAcessoNome: ['', [Validators.required, requiredTrim]],
      descricao: [''],
      idUsuario: [null, Validators.required],
      ativo: [true, Validators.required],
    });
  }

  ngOnInit(): void {
    this.carregarUsuarios(); // Carrega os usuários para o select
    const idParam = this.activatedRoute.snapshot.paramMap.get('idChaveAcesso');
    if (idParam) {
      this.idChaveAcesso = parseInt(idParam, 10);
      this.popularDadosChaveAcesso();
    }
  }

  carregarUsuarios(): void {
    this.usuarioService.getAll().subscribe({
      next: (response: ListaUsuarioResponse) => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          this.router.navigate(['/integracao/chave-acesso']);
          return;
        }

        this.listaUsuarios = response.listaUsuario;
      },
      error: () => {
        this._isLoading = false;
        this.alertService.show(
          'Erro inesperado',
          'Não foi possível carregar os dados da chave.',
          AlertType.error
        );
        this.router.navigate(['/integracao/chave-acesso']);
      },
    });
  }

  popularDadosChaveAcesso(): void {
    if (!this.idChaveAcesso) return;

    this._isLoading = true;
    this.chaveAcessoService.getChavePorId(this.idChaveAcesso).subscribe({
      next: (response: GetChaveAcessoResponse) => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          this.router.navigate(['/integracao/chave-acesso']);
          return;
        }

        this.model.patchValue(response.chaveAcesso);
        this.model.get('idUsuario')?.disable(); // Desabilita o campo usuário em modo de edição
      },
      error: () => {
        this._isLoading = false;
        this.alertService.show(
          'Erro inesperado',
          'Não foi possível carregar os dados da chave.',
          AlertType.error
        );
        this.router.navigate(['/integracao/chave-acesso']);
      },
    });
  }

  submit(): void {
    if (this.model.invalid) {
      this.alertService.show(
        'Atenção',
        'Por favor, preencha todos os campos obrigatórios.',
        AlertType.warning
      );
      return;
    }

    this._isLoading = true;
    const formValue = this.model.getRawValue();
    const request: ChaveAcessoRequest = {
      chaveAcessoNome: formValue.chaveAcessoNome,
      descricao: formValue.descricao,
      idUsuario: formValue.idUsuario,
      ativo: formValue.ativo,
    };

    if (this.idChaveAcesso) {
      this.atualizar(request);
    } else {
      this.criar(request);
    }
  }

  private criar(request: ChaveAcessoRequest): void {
    this.chaveAcessoService.criarChave(request).subscribe({
      next: (response: ReturnStruct) => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }
        this.alertService.show('Sucesso', 'Chave de acesso criada com sucesso!', AlertType.success);
        this.router.navigate(['/integracao/chave-acesso']);
      },
      error: error => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  private atualizar(request: ChaveAcessoRequest): void {
    if (!this.idChaveAcesso) return;

    this.chaveAcessoService.atualizarChave(this.idChaveAcesso, request).subscribe({
      next: (response: ReturnStruct) => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }
        this.alertService.show('Sucesso', 'Chave de acesso atualizada com sucesso!', AlertType.success);
        this.router.navigate(['/integracao/chave-acesso']);
      },
      error: error => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  cancelar(): void {
    this.router.navigate(['/integracao/chave-acesso']);
  }
}
